import { NcFileBatchEventsLink, NcFileBatchEventType } from '@v2/pages/common/enums';
import { createLevelSubScreenLinking } from '@v2/pages/common/helpers';
import { navigationLevels } from '@v2/pages/machines/common/constants';
import { BatchDetailsScreen, BatchDetailsScreenLink, BatchLevelSubScreen } from '@v2/pages/batch/details/common/enums/batchDetails.enum';
import {
  BatchDetailsBatch,
  BatchDetailsBatchDocuments,
  BatchDetailsBatchEventsActive,
  BatchDetailsBatchEventsChange,
  BatchDetailsBatchEventsCycle,
  BatchDetailsBatchEventsNavigator,
  BatchDetailsBatchEventsSetup,
  BatchDetailsBatchEventsTrace,
  BatchDetailsBatchTimeline,
} from '../modules/batch';
import { BatchDetailsJobs, BatchDetailsJobsDocuments, BatchDetailsJobsTimeline } from '@v2/pages/batch/details/modules/jobs';
import {
  BatchDetailsNCFile,
  BatchDetailsNCFileDocuments,
  BatchDetailsNCFileEventsActive,
  BatchDetailsNCFileEventsChange,
  BatchDetailsNCFileEventsCycle,
  BatchDetailsNCFileEventsNavigator,
  BatchDetailsNCFileEventsSetup,
  BatchDetailsNCFileEventsTrace,
  BatchDetailsNCFilePerformanceActive,
  BatchDetailsNCFilePerformanceNavigator,
  BatchDetailsNCFilePerformancePaid,
  BatchDetailsNCFilePerformanceProductive,
  BatchDetailsNCFilePerformanceUtilization,
  BatchDetailsNCFileTimeline,
  BatchDetailsNCFileTimes,
  BatchDetailsNCFileVersions,
  BatchDetailsNCFileTraceability,
} from '../modules/nc-file';
import { BatchDetailsNCFileLinks } from '@v2/pages/batch/details/modules/nc-file/BatchDetailsNcFileLinks';
import { BatchDetailsPart, BatchDetailsPartDocuments, BatchDetailsPartTimeline } from '@v2/pages/batch/details/modules/part';
import { BatchDetailsTools } from '@v2/pages/batch/details/modules/tools/BatchDetailsTools';
import { BatchDetailsBatchNavigator } from './BatchDetailsBatchNavigator';
import { BatchDetailsJobNavigator } from './BatchDetailsJobNavigator';
import { BatchDetailsNCFileNavigator } from './BatchDetailsNCFileNavigator';
import { BatchDetailsPartNavigator } from './BatchDetailsPartNavigator';
import { BatchDetailsToolNavigator } from './BatchDetailsToolNavigator';
import {
  filteredBatchLevelSubScreen,
  filteredBatchLevelSubScreenLink,
  filteredJobLevelSubScreen,
  filteredJobLevelSubScreenLink,
  filteredNCFileLevelSubScreen,
  filteredNCFileLevelSubScreenLink,
  filteredPartLevelSubScreen,
  filteredPartLevelSubScreenLink,
} from '../../../common/constants/base.constants';
import { timelineAvailable } from '@config/navigationRestrictions';
import { injectConditionalArrayItems } from '@helpers';
import { ToolLevelSubScreen, ToolLevelSubScreenLink } from '@v2/pages/tools/details/common/enums/toolsDetails.enum';
import { PartLevelSubScreen } from '@v2/pages/part/details/common/enums/partDetails.enum';
import { NCFileLevelSubScreen } from '@v2/pages/nc-file/details/common/enums/ncFileDetails.enum';
import { JobLevelSubScreen } from '@v2/pages/job/common/enums/jobDetails.enum';

export const BatchDetailsScreenName = 'BatchDetails';

export const batchDetailsLinking = {
  path: 'batch/details',
  screens: {
    [BatchDetailsScreen.PART]: {
      path: BatchDetailsScreenLink.PART,
      screens: createLevelSubScreenLinking(BatchDetailsScreen.PART, {
        levels: filteredPartLevelSubScreen,
        links: filteredPartLevelSubScreenLink,
      }),
    },
    [BatchDetailsScreen.BATCH]: {
      path: BatchDetailsScreenLink.BATCH,
      screens: createLevelSubScreenLinking(BatchDetailsScreen.BATCH, {
        levels: filteredBatchLevelSubScreen,
        links: filteredBatchLevelSubScreenLink,
        nestedScreens: {
          [BatchLevelSubScreen.TIMES]: {
            screenName: BatchLevelSubScreen.TIMES,
            items: NcFileBatchEventType,
            links: NcFileBatchEventsLink,
          },
        },
      }),
    },
    [BatchDetailsScreen.NC_FILE]: {
      path: BatchDetailsScreenLink.NC_FILE,
      screens: createLevelSubScreenLinking(BatchDetailsScreen.NC_FILE, {
        levels: filteredNCFileLevelSubScreen,
        links: filteredNCFileLevelSubScreenLink,
        nestedScreens: {
          [NCFileLevelSubScreen.TIMES]: {
            screenName: NCFileLevelSubScreen.TIMES,
            items: NcFileBatchEventType,
            links: NcFileBatchEventsLink,
          },
        },
      }),
    },
    [BatchDetailsScreen.JOBS]: {
      path: BatchDetailsScreenLink.JOBS,
      screens: createLevelSubScreenLinking(BatchDetailsScreen.JOBS, {
        levels: filteredJobLevelSubScreen,
        links: filteredJobLevelSubScreenLink,
      }),
    },
    [BatchDetailsScreen.TOOLS]: {
      path: BatchDetailsScreenLink.TOOLS,
      screens: createLevelSubScreenLinking(BatchDetailsScreen.TOOLS, {
        levels: ToolLevelSubScreen,
        links: ToolLevelSubScreenLink,
      }),
    },
  },
};

export const batchDetailsConfig = [
  { name: BatchDetailsScreen.PART, Component: BatchDetailsPartNavigator },
  { name: BatchDetailsScreen.BATCH, Component: BatchDetailsBatchNavigator },
  { name: BatchDetailsScreen.NC_FILE, Component: BatchDetailsNCFileNavigator },
  { name: BatchDetailsScreen.JOBS, Component: BatchDetailsJobNavigator },
  { name: BatchDetailsScreen.TOOLS, Component: BatchDetailsToolNavigator },
];

export const batchDetailsPartConfig = [
  {
    name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.GENERAL}`,
    Component: BatchDetailsPart,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.TIMELINE}`,
    Component: BatchDetailsPartTimeline,
  }),
  // {
  //   name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.TIMES}`,
  //   Component: BatchDetailsPart,
  // },
  // {
  //   name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.PERFORMANCE}`,
  //   Component: BatchDetailsPartPerformanceNavigator,
  // },
  // {
  //   name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.VERSIONS}`,
  //   Component: BatchDetailsPart,
  // },
  // {
  //   name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.LINKS}`,
  //   Component: BatchDetailsPart,
  // },
  {
    name: `${BatchDetailsScreen.PART}${PartLevelSubScreen.DOCUMENTS}`,
    Component: BatchDetailsPartDocuments,
  },
];

export const batchDetailsToolConfig = [
  {
    name: `${BatchDetailsScreen.TOOLS}${ToolLevelSubScreen.GENERAL}`,
    Component: BatchDetailsTools,
  },
];

export const batchDetailsBatchConfig = [
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.GENERAL}`,
    Component: BatchDetailsBatch,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMELINE}`,
    Component: BatchDetailsBatchTimeline,
  }),
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}`,
    Component: BatchDetailsBatchEventsNavigator,
  },
  // {
  //   name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.VERSIONS}`,
  //   Component: BatchDetailsBatch,
  // },
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.DOCUMENTS}`,
    Component: BatchDetailsBatchDocuments,
  },
];

export const batchDetailsNCFileConfig = [
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.GENERAL}`,
    Component: BatchDetailsNCFile,
  },
  // Based on task SF-4963
  // ...injectConditionalArrayItems(timelineAvailable, {
  //   name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMELINE}`,
  //   Component: BatchDetailsNCFileTimeline,
  // }),

  // {
  //   name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}`,
  //   Component: BatchDetailsNCFileTimes,
  // },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}`,
    Component: BatchDetailsNCFileEventsNavigator,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}`,
    Component: BatchDetailsNCFilePerformanceNavigator,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.VERSIONS}`,
    Component: BatchDetailsNCFileVersions,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.LINKS}`,
    Component: BatchDetailsNCFileLinks,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.DOCUMENTS}`,
    Component: BatchDetailsNCFileDocuments,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TRACEABILITY}`,
    Component: BatchDetailsNCFileTraceability,
  }
];

export const batchDetailsJobsConfig = [
  {
    name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.GENERAL}`,
    Component: BatchDetailsJobs,
  },
  ...injectConditionalArrayItems(timelineAvailable, {
    name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.TIMELINE}`,
    Component: BatchDetailsJobsTimeline,
  }),
  // {
  //   name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.TIMES}`,
  //   Component: BatchDetailsJobs,
  // },
  // {
  //   name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.PERFORMANCE}`,
  //   Component: BatchDetailsJobsPerformanceNavigator,
  // },
  // {
  //   name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.VERSIONS}`,
  //   Component: BatchDetailsJobs,
  // },
  // {
  //   name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.LINKS}`,
  //   Component: BatchDetailsJobs,
  // },
  {
    name: `${BatchDetailsScreen.JOBS}${JobLevelSubScreen.DOCUMENTS}`,
    Component: BatchDetailsJobsDocuments,
  },
];

export const ncFilePerformanceConfig = [
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${navigationLevels.metricTypes.active}`,
    Component: BatchDetailsNCFilePerformanceActive,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${navigationLevels.metricTypes.productive}`,
    Component: BatchDetailsNCFilePerformanceProductive,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${navigationLevels.metricTypes.paid}`,
    Component: BatchDetailsNCFilePerformancePaid,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.PERFORMANCE}${navigationLevels.metricTypes.utilization}`,
    Component: BatchDetailsNCFilePerformanceUtilization,
  },
];

export const ncFileEventsConfig = [
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.CYCLE}`,
    Component: BatchDetailsNCFileEventsCycle,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.ACTIVE}`,
    Component: BatchDetailsNCFileEventsActive,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.CHANGE}`,
    Component: BatchDetailsNCFileEventsChange,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.SETUP}`,
    Component: BatchDetailsNCFileEventsSetup,
  },
  {
    name: `${BatchDetailsScreen.NC_FILE}${NCFileLevelSubScreen.TIMES}${NcFileBatchEventType.TRACE}`,
    Component: BatchDetailsNCFileEventsTrace,
  },
];

export const batchEventsConfig = [
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.CYCLE}`,
    Component: BatchDetailsBatchEventsCycle,
  },
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.ACTIVE}`,
    Component: BatchDetailsBatchEventsActive,
  },
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.CHANGE}`,
    Component: BatchDetailsBatchEventsChange,
  },
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.SETUP}`,
    Component: BatchDetailsBatchEventsSetup,
  },
  {
    name: `${BatchDetailsScreen.BATCH}${BatchLevelSubScreen.TIMES}${NcFileBatchEventType.TRACE}`,
    Component: BatchDetailsBatchEventsTrace,
  },
];
